/**
 * SRM 模組路由 — 供應商／採購單列表／Create／Edit／異動／詳情／從 PO／排程建入庫／排程 create／edit。
 *
 * 註冊方式：由 adminRoutes.jsx import 合併至 children。
 * index 為供應商管理 hub（導覽卡）；hub／各列表父路由掛 read；create／edit／inbound 等掛對應 mutation。
 * 物料採購單：`create` 須在 `:id` 之前；`:id` 為 layout（無 element，預設 Outlet），
 * 詳情為 index，edit／amendment／inbound／arrival-schedules/* 皆為其子路由。
 * 非庫存採購掛在 `non-stock`，靜態路徑先於 `:id`。舊 `/misc-purchase` 轉到新路徑。
 */
import { Navigate } from 'react-router';

import {
  MISC_PURCHASE_ORDER_READ_CODES,
  PERM,
  PURCHASE_ORDER_READ_CODES,
  SUPPLIER_READ_CODES,
} from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import RequirePermission from '#/modules/infra/auth/RequirePermission';
import SrmMgmtHubPage from '#/modules/infra/pages/SrmMgmtHubPage';
import CreateArrivalScheduleStockReceiptPage from '#/modules/srmMgmt/purchaseOrder/pages/CreateArrivalScheduleStockReceiptPage';
import CreateEditArrivalSchedulePage from '#/modules/srmMgmt/purchaseOrder/pages/CreateEditArrivalSchedulePage';
import CreateEditPurchaseOrderPage from '#/modules/srmMgmt/purchaseOrder/pages/CreateEditPurchaseOrderPage';
import CreateEditMiscPurchaseOrderPage from
  '#/modules/srmMgmt/miscPurchaseOrder/pages/CreateEditMiscPurchaseOrderPage';
import CreateEditMiscPurchaseOrderAmendmentPage from
  '#/modules/srmMgmt/miscPurchaseOrder/pages/CreateEditMiscPurchaseOrderAmendmentPage';
import MiscPurchaseOrderDetailPage from
  '#/modules/srmMgmt/miscPurchaseOrder/pages/MiscPurchaseOrderDetailPage';
import MiscPurchaseOrderListPage from
  '#/modules/srmMgmt/miscPurchaseOrder/pages/MiscPurchaseOrderListPage';
import CreateEditSupplierPage from '#/modules/srmMgmt/supplier/pages/CreateEditSupplierPage';
import CreateEditPurchaseOrderAmendmentPage from
  '#/modules/srmMgmt/purchaseOrder/pages/CreateEditPurchaseOrderAmendmentPage';
import CreatePurchaseOrderStockReceiptPage from '#/modules/srmMgmt/purchaseOrder/pages/CreatePurchaseOrderStockReceiptPage';
import PurchaseOrderDetailPage from '#/modules/srmMgmt/purchaseOrder/pages/PurchaseOrderDetailPage';
import PurchaseOrderListGate from '#/modules/srmMgmt/purchaseOrder/pages/PurchaseOrderListGate';
import RedirectLegacyMiscPurchasePage from
  '#/modules/srmMgmt/miscPurchaseOrder/pages/RedirectLegacyMiscPurchasePage';
import SupplierListPage from '#/modules/srmMgmt/supplier/pages/SupplierListPage';

const SRM_HUB_READ_CODES = [
  ...PURCHASE_ORDER_READ_CODES,
  ...MISC_PURCHASE_ORDER_READ_CODES,
  ...SUPPLIER_READ_CODES,
];

const MATERIAL_PURCHASE_HUB_READ_CODES = [
  ...PURCHASE_ORDER_READ_CODES,
  ...MISC_PURCHASE_ORDER_READ_CODES,
];

export const srmRoutes = [
  {
    path: 'srm-mgmt',
    handle: { title: '供應商管理' },
    children: [
      {
        index: true,
        element: (
          <RequirePermission codes={SRM_HUB_READ_CODES} mode="any">
            <SrmMgmtHubPage />
          </RequirePermission>
        ),
      },
      {
        path: 'supplier',
        handle: { title: '供應商列表' },
        element: (
          <RequirePermission codes={SUPPLIER_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <SupplierListPage />,
          },
          {
            path: 'create',
            handle: { title: '新增供應商' },
            element: (
              <RequirePermission code={PERM.SUPPLIER_CREATE}>
                <CreateEditSupplierPage />
              </RequirePermission>
            ),
          },
          {
            path: ':id/edit',
            handle: { title: '編輯供應商' },
            element: (
              <RequirePermission code={PERM.SUPPLIER_UPDATE}>
                <CreateEditSupplierPage />
              </RequirePermission>
            ),
          },
        ],
      },
      {
        path: 'material-purchase',
        handle: { title: '物料採購單' },
        element: (
          <RequirePermission codes={MATERIAL_PURCHASE_HUB_READ_CODES} mode="any" />
        ),
        children: [
          {
            index: true,
            element: <PurchaseOrderListGate />,
          },
          {
            path: 'non-stock',
            handle: { title: '非庫存採購單' },
            element: (
              <RequirePermission codes={MISC_PURCHASE_ORDER_READ_CODES} mode="any" />
            ),
            children: [
              {
                index: true,
                element: <MiscPurchaseOrderListPage />,
              },
              {
                path: 'create',
                handle: { title: '新建非庫存採購單' },
                element: (
                  <RequirePermission code={PERM.MISC_PURCHASE_ORDER_CREATE}>
                    <CreateEditMiscPurchaseOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                handle: { title: '非庫存採購單詳情' },
                children: [
                  {
                    index: true,
                    element: <MiscPurchaseOrderDetailPage />,
                  },
                  {
                    path: 'edit',
                    handle: { title: '編輯非庫存採購單' },
                    element: (
                      <RequirePermission code={PERM.MISC_PURCHASE_ORDER_UPDATE}>
                        <CreateEditMiscPurchaseOrderPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'amendment/create',
                    handle: { title: '新建非庫存採購異動單' },
                    element: (
                      <RequirePermission code={PERM.MISC_PURCHASE_ORDER_UPDATE}>
                        <CreateEditMiscPurchaseOrderAmendmentPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'amendment/:amendmentId/edit',
                    handle: { title: '編輯非庫存採購異動單' },
                    element: (
                      <RequirePermission code={PERM.MISC_PURCHASE_ORDER_UPDATE}>
                        <CreateEditMiscPurchaseOrderAmendmentPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
            ],
          },
          {
            element: (
              <RequirePermission codes={PURCHASE_ORDER_READ_CODES} mode="any" />
            ),
            children: [
              {
                path: 'create',
                handle: { title: '新增物料採購單' },
                element: (
                  <RequirePermission code={PERM.PURCHASE_ORDER_CREATE}>
                    <CreateEditPurchaseOrderPage />
                  </RequirePermission>
                ),
              },
              {
                path: ':id',
                handle: { title: '物料採購單詳情' },
                children: [
                  {
                    index: true,
                    element: <PurchaseOrderDetailPage />,
                  },
                  {
                    path: 'edit',
                    handle: { title: '編輯物料採購單' },
                    element: (
                      <RequirePermission code={PERM.PURCHASE_ORDER_UPDATE}>
                        <CreateEditPurchaseOrderPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'amendment/create',
                    handle: { title: '新建物料採購異動單' },
                    element: (
                      <RequirePermission code={PERM.PURCHASE_ORDER_UPDATE}>
                        <CreateEditPurchaseOrderAmendmentPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'amendment/:amendmentId/edit',
                    handle: { title: '編輯物料採購異動單' },
                    element: (
                      <RequirePermission code={PERM.PURCHASE_ORDER_UPDATE}>
                        <CreateEditPurchaseOrderAmendmentPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'inbound/create',
                    handle: { title: '物料採購單新建入庫' },
                    element: (
                      <RequirePermission code={PERM.STOCK_RECEIPT_CREATE}>
                        <CreatePurchaseOrderStockReceiptPage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'arrival-schedules/create',
                    handle: { title: '新建交期排程' },
                    element: (
                      <RequirePermission code={PERM.PURCHASE_ORDER_CREATE}>
                        <CreateEditArrivalSchedulePage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'arrival-schedules/:scheduleId/edit',
                    handle: { title: '編輯交期排程' },
                    element: (
                      <RequirePermission code={PERM.PURCHASE_ORDER_UPDATE}>
                        <CreateEditArrivalSchedulePage />
                      </RequirePermission>
                    ),
                  },
                  {
                    path: 'arrival-schedules/:scheduleId/inbound/create',
                    handle: { title: '排程建入庫' },
                    element: (
                      <RequirePermission code={PERM.STOCK_RECEIPT_CREATE}>
                        <CreateArrivalScheduleStockReceiptPage />
                      </RequirePermission>
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: 'misc-purchase',
        element: <Navigate to={ROUTES.miscPurchaseOrder} replace />,
      },
      {
        path: 'misc-purchase/*',
        element: <RedirectLegacyMiscPurchasePage />,
      },
    ],
  },
];
